import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen } from "lucide-react";
import { SourcedStat } from "@/components/SourcedStat";
import { statsForContext, type SiteLocale } from "@/data/industryStats";
import { getRouteByKey } from "@/utils/routing";

interface IndustryProofStripProps {
  context: string;
}

export const IndustryProofStrip = ({ context }: IndustryProofStripProps) => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || "sk") as SiteLocale;
  const stats = statsForContext(context, locale);

  if (stats.length === 0) return null;

  const blogPath = getRouteByKey("blogDigitalizationGuide", locale);

  return (
    <section
      className="section-padding bg-gradient-to-br from-primary/5 via-background to-accent/5 border-y border-border/40"
      data-testid={`industry-proof-${context}`}
    >
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
          <p className="text-sm font-medium text-primary mb-3">{t("industryStats.strip.badge")}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            {t("industryStats.strip.title")}
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            {t("industryStats.strip.subtitle")}
          </p>
        </div>

        <div className={`grid gap-6 sm:gap-8 ${stats.length > 3 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"}`}>
          {stats.map((stat) => (
            <div
              key={stat.id}
              className="rounded-2xl border border-border/60 bg-card/80 p-5 sm:p-6 shadow-sm"
            >
              <SourcedStat id={stat.id} />
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-sm text-muted-foreground max-w-3xl mx-auto">
          {t("industryStats.strip.disclaimer")}
        </p>

        {blogPath && blogPath !== "/" && (
          <div className="mt-6 flex justify-center">
            <Link
              to={blogPath}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
              data-testid="industry-proof-blog-link"
            >
              <BookOpen className="h-4 w-4" />
              {t("industryStats.strip.blogCta")}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default IndustryProofStrip;
